import { getErrorMessage } from "./apiMessages";

export const BUSINESS_TYPES = [
  { value: "Travel Agent", label: "Travel Agent" },
  { value: "Tour Operator", label: "Tour Operator" },
  { value: "Umrah & Hajj Specialist", label: "Umrah & Hajj Specialist" },
  { value: "Sub-Agent", label: "Sub-Agent" },
  { value: "Other", label: "Other" },
];

export const LEGAL_STRUCTURES = [
  { value: "Sole Proprietorship", label: "Sole Proprietorship" },
  { value: "Partnership", label: "Partnership" },
  { value: "LLP", label: "LLP" },
  { value: "Company", label: "Company" },
  { value: "Other", label: "Other" },
];

export const KYC_DOCUMENT_TYPES = [
  { value: "gst_certificate", label: "GST certificate" },
  { value: "pan_card", label: "PAN card" },
  { value: "cancelled_cheque", label: "Cancelled cheque" },
  { value: "trade_license", label: "Trade license" },
  { value: "address_proof", label: "Address proof" },
  { value: "other", label: "Other" },
];

export const BANK_PROOF_DOCUMENT_TYPE = "cancelled_cheque";

export const ACCOUNT_TYPES = [
  { value: "Current", label: "Current" },
  { value: "Savings", label: "Savings" },
];

export const KYC_ACCEPT = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";
export const KYC_MAX_BYTES = 10 * 1024 * 1024;

const IFSC_PATTERN = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const ACCOUNT_NUMBER_PATTERN = /^[0-9]{9,18}$/;
const HOLDER_NAME_PATTERN = /^(?=.*[A-Za-z])[A-Za-z0-9][A-Za-z0-9 .,'&()/-]{1,99}$/;

const KYC_REQUIREMENTS = {
  "Sole Proprietorship": {
    gstRequired: false,
    documents: ["pan_card", "address_proof", "cancelled_cheque"],
  },
  Partnership: {
    gstRequired: true,
    documents: ["pan_card", "gst_certificate", "trade_license", "address_proof", "cancelled_cheque"],
  },
  LLP: {
    gstRequired: true,
    documents: ["pan_card", "gst_certificate", "trade_license", "address_proof", "cancelled_cheque"],
  },
  Company: {
    gstRequired: true,
    documents: ["pan_card", "gst_certificate", "trade_license", "address_proof", "cancelled_cheque"],
  },
  Other: {
    gstRequired: false,
    documents: ["pan_card", "address_proof", "cancelled_cheque", "other"],
  },
};

export function getKycRequirement(legalStructure) {
  return KYC_REQUIREMENTS[legalStructure] || KYC_REQUIREMENTS["Sole Proprietorship"];
}

export function kycTypeLabel(value) {
  return KYC_DOCUMENT_TYPES.find((item) => item.value === value)?.label || String(value || "Document").replace(/_/g, " ");
}

export function onboardingDocumentTypes(requirement) {
  return (requirement?.documents || []).filter((type) => type !== BANK_PROOF_DOCUMENT_TYPE);
}

export function latestDocumentForType(documents = [], documentType) {
  return (documents || []).reduce((latest, doc) => {
    if (String(doc.document_type || "") !== String(documentType || "")) {
      return latest;
    }
    if (doc.is_current === false) {
      return latest;
    }
    if (!latest || Number(doc.kyc_document_id || 0) > Number(latest.kyc_document_id || 0)) {
      return doc;
    }
    return latest;
  }, null);
}

export function documentStatusMeta(doc) {
  if (!doc) {
    return {
      value: "required",
      label: "Required",
      source: "",
    };
  }

  const status = String(doc.review_status || "").toLowerCase().replace(/[\s-]+/g, "_");
  if (status === "approved") {
    return { value: "approved", label: "Approved", source: "Approved by Super Admin" };
  }
  if (status === "rejected" || status === "action_required") {
    return { value: "rejected", label: "Rejected", source: "Rejected by Super Admin" };
  }
  return { value: "uploaded", label: "Uploaded", source: "Uploaded by agent" };
}

export function isRejectedDocument(doc) {
  const status = String(doc?.review_status || "").toLowerCase().replace(/[\s-]+/g, "_");
  return status === "rejected" || status === "action_required";
}

export function validateKycFile(file) {
  if (!file) {
    return getErrorMessage("KYC_FILE_REQUIRED");
  }
  const name = String(file.name || "");
  const mime = String(file.type || "");
  const allowedType = /pdf|jpeg|jpg|png/i.test(mime) || /\.(pdf|jpe?g|png)$/i.test(name);
  if (!allowedType) {
    return getErrorMessage("KYC_FILE_TYPE");
  }
  if (Number(file.size || 0) > KYC_MAX_BYTES) {
    return getErrorMessage("KYC_FILE_SIZE");
  }
  return "";
}

function trimBank(value) {
  return String(value || "").trim();
}

export function normalizeIfsc(value) {
  return trimBank(value).replace(/\s+/g, "").toUpperCase();
}

export function normalizeAccountNumber(value) {
  return trimBank(value).replace(/[\s-]/g, "");
}

export function maskAccountNumber(value) {
  const accountNumber = normalizeAccountNumber(value);
  if (!accountNumber) {
    return "";
  }
  if (accountNumber.length <= 4) {
    return `XXXX${accountNumber}`;
  }
  return `${"X".repeat(Math.max(4, accountNumber.length - 4))}${accountNumber.slice(-4)}`;
}

export function parseAgentBankDetails(raw) {
  const empty = {
    account_holder_name: "",
    account_number: "",
    ifsc_code: "",
    bank_name: "",
    branch_name: "",
    account_type: "",
  };
  const text = trimBank(raw);
  if (!text) {
    return empty;
  }
  if (text.startsWith("{")) {
    try {
      const json = JSON.parse(text);
      return {
        account_holder_name: trimBank(json.account_holder_name || json.accountHolderName),
        account_number: normalizeAccountNumber(json.account_number || json.accountNumber),
        ifsc_code: normalizeIfsc(json.ifsc_code || json.ifscCode || json.ifsc),
        bank_name: trimBank(json.bank_name || json.bankName),
        branch_name: trimBank(json.branch_name || json.branchName),
        account_type: trimBank(json.account_type || json.accountType),
      };
    } catch (_error) {
      return empty;
    }
  }
  if (!text.includes(":")) {
    return empty;
  }
  const parsed = { ...empty };
  const aliases = {
    "account holder name": "account_holder_name",
    "account number": "account_number",
    "ifsc code": "ifsc_code",
    ifsc: "ifsc_code",
    "bank name": "bank_name",
    "branch name": "branch_name",
    "account type": "account_type",
  };
  text.split(/\r?\n/).forEach((line) => {
    const match = line.match(/^([^:]+):\s*(.*)$/);
    if (!match) return;
    const key = aliases[match[1].trim().toLowerCase()];
    if (key) parsed[key] = match[2].trim();
  });
  parsed.account_number = normalizeAccountNumber(parsed.account_number);
  parsed.ifsc_code = normalizeIfsc(parsed.ifsc_code);
  return parsed;
}

export function serializeAgentBankDetails(values = {}) {
  const bank = {
    account_holder_name: trimBank(values.account_holder_name),
    account_number: normalizeAccountNumber(values.account_number),
    ifsc_code: normalizeIfsc(values.ifsc_code),
    bank_name: trimBank(values.bank_name),
    branch_name: trimBank(values.branch_name || values.bank_branch_name),
    account_type: trimBank(values.account_type),
  };

  if (!Object.values(bank).some(Boolean)) {
    return "";
  }

  const lines = [
    `Account Holder Name: ${bank.account_holder_name}`,
    `Account Number: ${bank.account_number}`,
    `IFSC Code: ${bank.ifsc_code}`,
    `Bank Name: ${bank.bank_name}`,
  ];
  if (bank.branch_name) {
    lines.push(`Branch Name: ${bank.branch_name}`);
  }
  lines.push(`Account Type: ${bank.account_type}`);
  return lines.join("\n");
}

export function validateAgentBankDetails(values) {
  const errors = {};
  const holder = trimBank(values.account_holder_name);
  const accountNumber = normalizeAccountNumber(values.account_number);
  const confirm = normalizeAccountNumber(values.confirm_account_number);
  const ifsc = normalizeIfsc(values.ifsc_code);
  const bankName = trimBank(values.bank_name);
  const accountType = trimBank(values.account_type);

  if (!holder) errors.account_holder_name = "Account holder name is required.";
  else if (!HOLDER_NAME_PATTERN.test(holder)) errors.account_holder_name = "Enter a valid account holder name.";

  if (!accountNumber) errors.account_number = "Account number is required.";
  else if (!ACCOUNT_NUMBER_PATTERN.test(accountNumber)) errors.account_number = "Enter a valid account number (9-18 digits).";

  if (!confirm) errors.confirm_account_number = "Confirm the account number.";
  else if (confirm !== accountNumber) errors.confirm_account_number = "Account number confirmation does not match.";

  if (!ifsc) errors.ifsc_code = "IFSC code is required.";
  else if (!IFSC_PATTERN.test(ifsc)) errors.ifsc_code = "Enter a valid IFSC code.";

  if (!bankName) errors.bank_name = "Bank name is required.";

  if (!accountType) errors.account_type = "Select an account type.";

  return errors;
}

export function getAgentAccessLevel(profile) {
  if (profile?.access_level || profile?.accessLevel) {
    return profile.access_level || profile.accessLevel;
  }

  const status = String(profile?.status || "").trim().toLowerCase();
  const verification = String(profile?.verification_status || profile?.verificationStatus || "").trim().toLowerCase();

  if (["inactive", "suspended"].includes(status)) {
    return "blocked";
  }
  if (status !== "active") {
    return "waiting_approval";
  }
  if (verification === "verified") {
    return "dashboard";
  }
  return "onboarding";
}

export function getAgentHomePath(profile) {
  const level = getAgentAccessLevel(profile);
  if (level === "waiting_approval") {
    return "/waiting-approval";
  }
  if (level === "onboarding") {
    return "/onboarding";
  }
  if (level === "blocked") {
    return "/login";
  }
  return "/packages";
}

export function formatOtpCountdown(seconds) {
  const safe = Math.max(0, Number(seconds) || 0);
  const minutes = Math.floor(safe / 60);
  const remainder = safe % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
}
